import { apiClient } from './base';

/** A logger's own latest reported position (Hardware page map). */
export interface LoggerLocation {
  LoggerID: string;
  SerialNumber?: string;
  DeviceName?: string;
  TrackStatus?: string;
  Latitude: number;
  Longitude: number;
  LocationAccuracy?: number;
  LocationMethod?: string;
  LocationTime?: string;
}

export interface Logger {
  LoggerID: string;
  SerialNumber: string;
  E2EManufactureNumber?: string;
  HMTechNumber?: string;
  // Company the device is assigned to: the buyer (For Sale) or the hirer
  // (For Hire). HDTECH ("1") or blank means unassigned.
  CompanyID?: string;
  CurrentCompanyID?: string;
  CompanyName?: string;
  Assignment?: HardwareAssignment;
  HireOverdue?: boolean;
  // Bound to a pending or in-progress pour (a sold device's company is locked).
  OnLivePour?: boolean;
  Type?: string;
  TypeName?: string;
  SIMCardNumber?: string;
  SIMDataNumber?: string;
  SaleOrHire?: string;
  Status?: string;
  Note?: string;
  ManufacturedDate?: string;
  // Expected return date while on hire
  ReturnDate?: string;
  SoldDate?: string;
  PaymentTerms?: string;
  CurrentPourID?: string;
  CurrentPourCode?: string;
  HireStartDate?: string;
  // Actual return date of the last hire
  HireEndDate?: string;
  RepairStartDate?: string;
  HireID?: string;
  AccessCode?: string;
  ServiceCode?: string;
  CalibrationFrequencyNumber?: number;
  CalibrationFrequencyUnit?: string;
  CalibrationEffectiveDate?: string;
  TrackStatus?: string;
  BatteryLevel?: number;
  LastHourDataChannel1?: number;
  LastHourDataChannel2?: number;
  LastHourDataChannel3?: number;
  LastHourDataChannel4?: number;
  ConnectionType?: string;
  Firmware?: string;
  SignalStrengh?: number;
  WSStationID?: string;
  DeviceName?: string;
  Manufacture?: string;
  Mode?: string;
  // The weather-station credentials are never sent back: only whether each is stored.
  WSApiKeySet?: boolean;
  WSApiSecretSet?: boolean;
  Gateway?: string;
  LoggerFrequency?: string;
  DataTime1?: string;
  DataTime2?: string;
  DataTime3?: string;
  DataTime4?: string;
  ChannelID1?: string;
  ChannelID2?: string;
  ChannelID3?: string;
  ChannelID4?: string;
  Latitude?: number;
  Longitude?: number;
  LocationAccuracy?: number;
  LocationMethod?: string;
  LocationTime?: string;
  // Per-channel sensor type ('TEMPERATURE' | 'HUMIDITY'); null = unknown/unpopulated
  ChannelType1?: string | null;
  ChannelType2?: string | null;
  ChannelType3?: string | null;
  ChannelType4?: string | null;
  LicenceTypeID?: string | null;
  LicenceTypeName?: string | null;
  // Licence subscription; LicenceEndDate = start + months (calculated)
  LicenceStartDate?: string | null;
  SubscriptionMonths?: number | null;
  LicenceEndDate?: string | null;
  LicenceExpired?: boolean;
  // Calculated: last calibration + calibration frequency (months)
  NextCalibrationDue?: string | null;
  // ConXagent SIM card
  SIMStatus?: string | null;
  SIMType?: string | null;
}

// Billing licence tier assigned per logger. Purely a billing/reporting
// construct — does not gate pour functions.
export interface LicenceType {
  LicenceTypeID: string;
  Name: string;
  CoveredFunctions?: string | null;
  SortOrder?: number | null;
  IsActive?: boolean | null;
  LoggerCount?: number | null;
}

export interface LicenceTypeCreate {
  Name: string;
  CoveredFunctions?: string;
  SortOrder?: number;
  IsActive?: boolean;
}

export interface LicenceTypeUpdate {
  Name?: string;
  CoveredFunctions?: string;
  SortOrder?: number;
  IsActive?: boolean;
}

export interface LoggerCreate {
  LoggerID?: string;
  SerialNumber: string;
  E2EManufactureNumber?: string;
  HMTechNumber?: string;
  CompanyID?: string;
  Type?: string;
  SIMCardNumber?: string;
  SIMDataNumber?: string;
  SaleOrHire?: string;
  Status?: string;
  Note?: string;
  ManufacturedDate?: string;
  PaymentTerms?: string;
  AccessCode?: string;
  ServiceCode?: string;
  CalibrationFrequencyNumber?: number;
  CalibrationFrequencyUnit?: string;
  CalibrationEffectiveDate?: string;
  Firmware?: string;
  Mode?: string;
  Gateway?: string;
  LoggerFrequency?: string;
  DeviceName?: string;
  Manufacture?: string;
  WSStationID?: string;
  WSApiKey?: string;
  WSApiSecret?: string;
  LicenceTypeID?: string;
}

export interface LoggerUpdate {
  SerialNumber?: string;
  E2EManufactureNumber?: string;
  HMTechNumber?: string;
  // Company sold or hired to; empty string unassigns it.
  CompanyID?: string;
  HireStartDate?: string;
  // Expected return of the hire
  ReturnDate?: string;
  // Resend after the live-pour warning for a hire company change
  ConfirmLivePour?: boolean;
  Type?: string;
  SIMCardNumber?: string;
  SIMDataNumber?: string;
  SaleOrHire?: string;
  Status?: string;
  Note?: string;
  ManufacturedDate?: string;
  SoldDate?: string;
  PaymentTerms?: string;
  AccessCode?: string;
  ServiceCode?: string;
  CalibrationFrequencyNumber?: number;
  CalibrationFrequencyUnit?: string;
  CalibrationEffectiveDate?: string;
  Firmware?: string;
  Mode?: string;
  Gateway?: string;
  LoggerFrequency?: string;
  DeviceName?: string;
  Manufacture?: string;
  WSStationID?: string;
  // Left out keeps the stored value; "" clears it.
  WSApiKey?: string;
  WSApiSecret?: string;
  // Empty string clears the licence
  LicenceTypeID?: string;
  LicenceStartDate?: string;
  // 3, 6 or 12; 0 clears
  SubscriptionMonths?: number;
  SIMStatus?: string;
  SIMType?: string;
}

/** Result of a mode or frequency change. */
export interface DeviceActionResult {
  logger_id: string;
  // False when the change was only saved on our side
  sent_to_device: boolean;
  message: string;
}

export interface BulkAddPayload {
  company_id: string;
  sale_or_hire?: string;
  items: { temptrack_id: number; hm_tech_number: string; device_type?: string }[];
}

export interface BulkAddResponse {
  results: {
    temptrack_id: number;
    hm_tech_number: string;
    ok: boolean;
    logger_id?: string | null;
    error?: string | null;
  }[];
  added: number;
  failed: number;
}

/** Fields a super admin can set on many devices at once. Only the
 *  fields sent change; Mode is never bulk-changed. */
export interface BulkUpdateFields {
  Status?: string;
  // "" unassigns (HDTECH)
  CompanyID?: string;
  SaleOrHire?: string;
  HireStartDate?: string;
  ReturnDate?: string;
  // "" clears
  LicenceTypeID?: string;
  LicenceStartDate?: string;
  // 0 clears
  SubscriptionMonths?: number;
  SIMStatus?: string;
  SIMType?: string;
  CalibrationFrequencyNumber?: number;
  LoggerFrequency?: string;
  Note?: string;
}

export interface BulkUpdatePayload {
  logger_ids: string[];
  fields: BulkUpdateFields;
  note_mode?: 'append' | 'replace';
  confirm_live_pour?: boolean;
}

export interface BulkUpdateResult {
  logger_id: string;
  hm_tech_number?: string | null;
  ok: boolean;
  error?: string | null;
  // 'LIVE_POUR' when a hire company change needs confirm_live_pour
  code?: string | null;
  pour_code?: string | null;
  // Fields that don't apply to this device's type
  skipped: string[];
}

export interface BulkUpdateResponse {
  results: BulkUpdateResult[];
  updated: number;
  failed: number;
}

export interface LoggerListResponse {
  loggers: Logger[];
  total: number;
  page: number;
  size: number;
  pages: number;
}

/** Who has the device: SOLD or ON_HIRE to its company, ASSIGNED (a company
 *  but no Sale or Hire set), IN_STOCK (still HDTECH's). */
export type HardwareAssignment = 'SOLD' | 'ON_HIRE' | 'ASSIGNED' | 'IN_STOCK';

export interface LoggerSearchParams {
  page?: number;
  size?: number;
  search?: string;
  logger_type?: string;
  status?: string;
  company_id?: string;
}

export interface LoggerStatus {
  logger_id: string;
  status: string;
  track_status: string;
  battery_level: number;
  signal_strength: number;
  connection_type: string;
  firmware: string;
  last_hour_data: {
    channel1?: number;
    channel2?: number;
    channel3?: number;
    channel4?: number;
  };
  data_times: {
    channel1?: string;
    channel2?: string;
    channel3?: string;
    channel4?: string;
  };
}

/**
 * Hardware API service
 */
export const hardwareAPI = {
  /**
   * Get loggers with pagination and filtering
   */
  getLoggers: async (params?: LoggerSearchParams): Promise<LoggerListResponse> => {
    const response = await apiClient.get('/hardware/loggers', { params });
    return response.data;
  },

  /** Fleet-wide status counts (UP/SUSPECT/DOWN/UNKNOWN + total) honouring
   *  search/type filters — for the status summary cards. */
  getLoggerStatusSummary: async (
    params?: Pick<LoggerSearchParams, 'search' | 'logger_type' | 'company_id'>,
  ): Promise<{ UP: number; SUSPECT: number; DOWN: number; UNKNOWN: number; total: number }> => {
    const response = await apiClient.get('/hardware/loggers/status-summary', { params });
    return response.data;
  },

  /** Loggers that have reported their own position, for the Hardware page map. */
  getLoggerLocations: async (
    params?: Pick<LoggerSearchParams, 'company_id'>,
  ): Promise<LoggerLocation[]> => {
    const response = await apiClient.get('/hardware/loggers/locations', { params });
    return response.data;
  },

  /** Ask TempTrack to send the LOCATION command so the logger takes a
   *  position fix now. Resolves once the command is accepted; the fix
   *  itself arrives later through the status stream. */
  requestLocation: async (
    loggerId: string,
  ): Promise<{ logger_id: string; serial_number?: string; message: string }> => {
    const response = await apiClient.post(`/hardware/loggers/${loggerId}/locate`);
    return response.data;
  },

  /** ConXagent only: send TempTrack COMM_MODE (NONE / ZIGBEE / MOBILE /
   *  GATEWAY). Applied at the logger's next check-in. */
  changeMode: async (loggerId: string, mode: string): Promise<DeviceActionResult> => {
    const response = await apiClient.post(`/hardware/loggers/${loggerId}/mode`, { mode });
    return response.data;
  },

  /** Set the logging frequency (a TempTrack TimeInterval name). Saved on
   *  our side; sent to the logger only when the backend has that enabled. */
  changeFrequency: async (loggerId: string, frequency: string): Promise<DeviceActionResult> => {
    const response = await apiClient.post(`/hardware/loggers/${loggerId}/frequency`, { frequency });
    return response.data;
  },

  /** Register several TempTrack discoveries to one client. */
  bulkAdd: async (payload: BulkAddPayload): Promise<BulkAddResponse> => {
    const response = await apiClient.post('/hardware/loggers/bulk', payload);
    return response.data;
  },

  /**
   * Apply the same field changes to several devices (super admin).
   * Results are per device; one failure does not stop the rest.
   */
  bulkUpdate: async (payload: BulkUpdatePayload): Promise<BulkUpdateResponse> => {
    const response = await apiClient.post('/hardware/loggers/bulk-update', payload);
    return response.data;
  },

  /**
   * Get logger by ID
   */
  getLoggerById: async (loggerId: string): Promise<Logger> => {
    const response = await apiClient.get(`/hardware/loggers/${loggerId}`);
    return response.data;
  },

  /**
   * Create new logger
   */
  createLogger: async (loggerData: LoggerCreate): Promise<Logger> => {
    const response = await apiClient.post('/hardware/loggers', loggerData);
    return response.data;
  },

  /**
   * Update logger
   */
  updateLogger: async (loggerId: string, loggerData: LoggerUpdate): Promise<Logger> => {
    const response = await apiClient.put(`/hardware/loggers/${loggerId}`, loggerData);
    return response.data;
  },

  /**
   * Delete logger (soft delete). For TempTrack device types the backend
   * also unregisters the device upstream (it drops back to a discovery);
   * `temptrack_unregistered` is false when that upstream call failed.
   */
  deleteLogger: async (
    loggerId: string,
  ): Promise<{ message: string; temptrack_unregistered: boolean | null }> => {
    const response = await apiClient.delete(`/hardware/loggers/${loggerId}`);
    return response.data;
  },

  /**
   * Get logger status information
   */
  getLoggerStatus: async (loggerId: string): Promise<LoggerStatus> => {
    const response = await apiClient.get(`/hardware/loggers/${loggerId}/status`);
    return response.data;
  },

  /**
   * Get available logger types
   */
  getLoggerTypes: async (): Promise<string[]> => {
    const response = await apiClient.get('/hardware/types');
    // Backend returns {types: string[]} so extract the array
    return response.data.types || [];
  },

  /**
   * List billing licence tiers (with per-tier logger counts)
   */
  getLicenceTypes: async (): Promise<LicenceType[]> => {
    const response = await apiClient.get('/hardware/licence-types');
    return response.data;
  },

  /**
   * Create a licence tier (Super Admin only)
   */
  createLicenceType: async (data: LicenceTypeCreate): Promise<LicenceType> => {
    const response = await apiClient.post('/hardware/licence-types', data);
    return response.data;
  },

  /**
   * Update a licence tier (Super Admin only)
   */
  updateLicenceType: async (id: string, data: LicenceTypeUpdate): Promise<LicenceType> => {
    const response = await apiClient.put(`/hardware/licence-types/${id}`, data);
    return response.data;
  },

  /**
   * Delete a licence tier (Super Admin only). Tiers still assigned to
   * loggers are deactivated instead; the result says which happened.
   */
  deleteLicenceType: async (
    id: string,
  ): Promise<{ found: boolean; deleted: boolean; deactivated: boolean; logger_count: number }> => {
    const response = await apiClient.delete(`/hardware/licence-types/${id}`);
    return response.data;
  },

  /**
   * List TempTrack discoveries (devices the server has seen but which
   * we haven't promoted yet). Each entry is annotated with whether a
   * matching active LoggerInfo row already exists on our side.
   */
  listTemptrackDiscovered: async (
    deviceType: 'loggers' | 'gateways' | 'routers' = 'loggers',
  ): Promise<TemptrackDiscoveryList> => {
    const response = await apiClient.get('/hardware/temptrack/discovered', {
      params: { device_type: deviceType },
    });
    return response.data;
  },

  /**
   * Promote a TempTrack discovery into an active logger. Creates the
   * matching LoggerInfo row on our side; rolls back the TempTrack-side
   * register if our DB insert fails.
   */
  registerTemptrackLogger: async (payload: RegisterLoggerPayload): Promise<Logger> => {
    const response = await apiClient.post('/hardware/temptrack/register', payload);
    return response.data;
  },
};

// ----------------------------------------------------------------------
// TempTrack integration types
// ----------------------------------------------------------------------
export interface TemptrackDiscoveryItem {
  temptrack_id: number;
  serial_number: string;
  metadata_version?: number;
  config_version?: number;
  already_registered: boolean;
  existing_logger_id?: string;
}

export interface TemptrackDiscoveryList {
  device_type: string;
  items: TemptrackDiscoveryItem[];
}

export interface RegisterLoggerPayload {
  // Required: TempTrack + identity
  temptrack_id: number;
  device_type: 'loggers' | 'gateways' | 'routers';
  hm_tech_number: string;
  // Optional override — normally omitted; the backend derives the fine
  // LoggerInfo.Type code from the device serial prefix.
  type_code?: string;
  company_id?: string;

  // Optional LoggerInfo fields the Add Hardware form supplies
  status?: string;
  note?: string;
  sale_or_hire?: string;
  manufactured_date?: string; // ISO datetime string
  payment_terms?: string;
  calibration_frequency_number?: number;
  calibration_frequency_unit?: string;
  logger_frequency?: string;
  sim_card_number?: string;
  sim_data_number?: string;
  access_code?: string;
  service_code?: string;
  device_name?: string;
  manufacture?: string;
  ws_station_id?: string;
  ws_api_key?: string;
  ws_api_secret?: string;
}